import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { ScreenModesProvider } from "@/providers/ScreenModesProvider";
import { ApiConfigProvider } from "@/providers/ApiConfigProvider";
import { CompanyQuerySync } from "@/components/CompanyQuerySync";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { CompanyProvider } from "@/contexts/CompanyContext";
import { FeedbackProvider } from "@/contexts/FeedbackContext";
import { AuthProvider } from "@/contexts/auth-context";
import { IntercomProvider } from "@/providers/IntercomProvider";
import App from "@/App";
import config from "@/appsettings.json";
import { PaySuiteConfigProvider } from "@/providers/PaySuiteConfigProvider";
import type { CompanyBasicInfoApi } from "@/types/api/user-context";
import type { IntercomUser } from "@/types/intercom";
import type { User } from "@/types/roles";

export type HostApp = 'app' | 'app-prod';

export interface AppContainerSettings {
  apiUrl?: string;
  host?: HostApp;
  /** External companies from userContext (used when host is 'app-prod') */
  externalCompanies?: CompanyBasicInfoApi[];
  /** Whether external companies are still loading */
  externalCompaniesLoading?: boolean;
  /** Callback to refetch user context from the host app (e.g. after company creation) */
  onRefreshUserContext?: () => Promise<void>;
  /** Account token for the Feature Feedback collector (from PidgetSettings) */
  feedbackAccountToken?: string;
  /** API host for the Feature Feedback collector (from PidgetSettings) */
  feedbackApiHost?: string;
  /** MFE version for the Feature Feedback collector (from PidgetSettings) */
  feedbackMfeVersion?: string;
  /** Number of days before re-displaying the feedback collector (from PidgetSettings) */
  feedbackReDisplayAfterDays?: string;
  /** When false, all screens are forced to live mode and demo-related UI is hidden */
  canUseDemoData?: boolean;
  /** When true, allows force-syncing data from ERP */
  canForceSync?: boolean;
  /** Intercom user info for identity verification */
  intercomUser?: IntercomUser | null;
  /** Intercom app ID */
  intercomAppId?: string;
  /** Cookie consent results from app-prod */
  cookieConsent?: Record<string, boolean> | null;
  /** Current user injected by the host wrapper. Drives role-based UI in credit-control. */
  currentUser?: User;
  /** Environment-specific PaySuite orchestrator component name (from PidgetSettings) */
  paysuiteOrchestratorName?: string;
}

interface AppContainerProps {
  settings?: AppContainerSettings;
}

export default function AppContainer({ settings }: AppContainerProps) {
  // Use settings.apiUrl if provided (from app-prod), otherwise fall back to local config
  const apiBaseUrl = settings?.apiUrl || config.apiBaseUrl;

  return (
    <AuthProvider user={settings?.currentUser}>
    <ScreenModesProvider canUseDemoData={settings?.canUseDemoData} canForceSync={settings?.canForceSync}>
      <QueryProvider>
        <CompanyProvider
          externalCompanies={settings?.externalCompanies}
          externalCompaniesLoading={settings?.externalCompaniesLoading}
          refreshExternalCompanies={settings?.onRefreshUserContext}
        >
          <ApiConfigProvider apiBaseUrl={apiBaseUrl}>
            <PaySuiteConfigProvider orchestratorName={settings?.paysuiteOrchestratorName}>
              <IntercomProvider user={settings?.intercomUser ?? null} intercomAppId={settings?.intercomAppId}>
                <FeedbackProvider accountToken={settings?.feedbackAccountToken} apiHost={settings?.feedbackApiHost} mfeVersion={settings?.feedbackMfeVersion} reDisplayAfterDays={settings?.feedbackReDisplayAfterDays} enabled={!settings?.cookieConsent || settings.cookieConsent['Feature Feedback Collector'] !== false}>
                  <CompanyQuerySync>
                    <TooltipProvider>
                      <ThemeProvider attribute="class" defaultTheme="light" forcedTheme="light" disableTransitionOnChange>
                        <Toaster richColors duration={5000} closeButton position="bottom-right" />
                        <App />
                      </ThemeProvider>
                    </TooltipProvider>
                  </CompanyQuerySync>
                </FeedbackProvider>
              </IntercomProvider>
            </PaySuiteConfigProvider>
          </ApiConfigProvider>
        </CompanyProvider>
      </QueryProvider>
    </ScreenModesProvider>
    </AuthProvider>
  );
}
